import { computed, observable } from 'mobx'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { $RootStore } from '../../../src/stores/RootStore'

// Coerce an untrusted form value (string/float) to a non-negative integer in minor units.
const toMinor = (v: unknown) => {
  const n = Math.floor(Number(v))
  return Number.isFinite(n) ? n : 0
}
const minorOrUndef = (v: unknown) => (v == null ? undefined : toMinor(v))

export const defaultData = {
  _id: '',
  name: '',
  category: '',
  price: 0,
  duration: 0,
  bufferTime: 0,
  showOnPortal: true,
  description: '',
  order: undefined,
  locations: [],
  schedule: undefined,
  memberOverrides: [],
}

export type $ExternalTreatmentModel = ExternalTreatmentModel

export type MemberOverrideValues = {
  price?: number | null
  duration?: number | null
  canPerformTreatment: boolean
}

export const normaliseMemberOverride = (
  base: { price: number, duration: number },
  professionalId: string,
  values: MemberOverrideValues,
): TreatmentMemberInfo | null => {
  const price = values.price == null || values.price === base.price ? null : values.price
  const duration = values.duration == null || values.duration === base.duration ? null : values.duration
  const isDefault = price == null && duration == null && values.canPerformTreatment

  return isDefault ? null : { professionalId, price, duration, canPerformTreatment: values.canPerformTreatment }
}

export const sameMemberOverride = (a: TreatmentMemberInfo | null, b: TreatmentMemberInfo | null) =>
  a?.price === b?.price
  && a?.duration === b?.duration
  && (a?.canPerformTreatment ?? true) === (b?.canPerformTreatment ?? true)

export default class ExternalTreatmentModel extends ModelStore {
  @observable accessor data: ExternalTreatment = { ...defaultData }
  fetchMethodName = 'externalTreatment'
  removeMethodName = 'deleteExternalTreatment'
  saveMethodName = 'addOrUpdateExternalTreatment'
  savedMemberOverrides: TreatmentMemberInfo[] = []

  constructor(data: Partial<ExternalTreatment>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
    this.savedMemberOverrides = this.data.memberOverrides ?? []
  }

  priceForLocation = (locationId: string | undefined) => {
    return this.data.locations.find(l => l.locationId === locationId)?.price || this.data.price
  }

  memberOverrideFor = (professionalId: string | undefined) => {
    if (!professionalId) return undefined
    return (this.data.memberOverrides ?? []).find(o => o.professionalId === professionalId)
  }

  // Effective price/duration/availability for a given member, falling back to the
  // location/base values when the member has no override (or none is allocated).
  priceForMember = (professionalId: string | undefined, locationId: string | undefined) => {
    const override = this.memberOverrideFor(professionalId)
    if (override?.price != null) return override.price
    return this.priceForLocation(locationId)
  }

  durationForMember = (professionalId: string | undefined) => {
    return this.memberOverrideFor(professionalId)?.duration ?? this.data.duration
  }

  canMemberPerform = (professionalId: string | undefined) => {
    const override = this.memberOverrideFor(professionalId)
    return override ? override.canPerformTreatment : true
  }

  // --- Per-member editing: single source of truth for the override<->base projection. ---

  // Effective values to show for a member in the editor: their override if set, otherwise
  // the base treatment.
  resolveMemberOverride = (memberId: string) => {
    const override = this.memberOverrideFor(memberId)
    const { price, duration } = this.data

    return {
      price: override?.price ?? price,
      duration: override?.duration ?? duration,
      canPerform: this.canMemberPerform(memberId),
      priceDeviates: override?.price != null && override.price !== price,
      durationDeviates: override?.duration != null && override.duration !== duration,
    }
  }

  setMemberOverride = (memberId: string, values: MemberOverrideValues) => {
    const base = { price: toMinor(this.data.price), duration: toMinor(this.data.duration) }
    const next = normaliseMemberOverride(base, memberId, values)
    const overrides = this.data.memberOverrides ?? []
    const exists = overrides.some(o => o.professionalId === memberId)

    const updated = exists
      ? overrides.flatMap(o => o.professionalId === memberId ? (next ? [next] : []) : [o])
      : next ? [...overrides, next] : overrides

    this.set({ memberOverrides: updated })
  }

  resetMemberToDefault = (memberId: string) => {
    this.setMemberOverride(memberId, { canPerformTreatment: this.canMemberPerform(memberId) })
  }

  // Apply a single-field change as a sparse override: store price/duration only when they
  // differ from the base, and drop the override entirely once the member is back to default.
  applyMemberPatch = (
    memberId: string,
    patch: Partial<Pick<TreatmentMemberInfo, 'price' | 'duration' | 'canPerformTreatment'>>,
  ) => {
    const existing = this.memberOverrideFor(memberId)

    // Resolve each field (patch wins, else the existing override, else base) and coerce to a
    // clean integer here so `data` always holds canonical overrides.
    this.setMemberOverride(memberId, {
      price: toMinor(patch.price ?? existing?.price ?? this.data.price),
      duration: Math.max(0, toMinor(patch.duration ?? existing?.duration ?? this.data.duration)),
      canPerformTreatment: patch.canPerformTreatment ?? existing?.canPerformTreatment ?? true,
    })
  }

  saveArgs = (): { data: ExternalTreatmentInput } => {
    // professionalId is fetched for scoping but isn't part of ExternalTreatmentInput.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { locations = [], professionalId, memberOverrides = [], ...rest } = this.data
    const base = { price: toMinor(this.data.price), duration: toMinor(this.data.duration) }

    const data: ExternalTreatmentInput = {
      ...rest,
      name: this.data.name.trim(),
      category: this.data.category.trim(),
      price: base.price,
      locations: locations.map(l => ({ ...l, price: minorOrUndef(l.price) })),
      memberOverrides: this.memberOverrideChanges(base, memberOverrides),
    }

    return { data }
  }

  private memberOverrideChanges = (base: { price: number, duration: number }, current: TreatmentMemberInfo[]) => {
    const isNew = !this.data._id
    const normalise = (override: TreatmentMemberInfo) => normaliseMemberOverride(base, override.professionalId, {
      ...override,
      duration: this.hasPhases ? null : override.duration,
    })

    const next = new Map(current.map(o => [o.professionalId, normalise(o)]))
    const saved = new Map(this.savedMemberOverrides.map(o => [o.professionalId, normaliseMemberOverride(base, o.professionalId, o)]))
    const memberIds = [...new Set([...saved.keys(), ...next.keys()])]

    return memberIds.flatMap(memberId => {
      const override = next.get(memberId) ?? null
      const unchanged = isNew ? !override : sameMemberOverride(saved.get(memberId) ?? null, override)
      if (unchanged) return []

      return [{
        professionalId: memberId,
        price: override?.price ?? null,
        duration: override?.duration ?? null,
        canPerformTreatment: override?.canPerformTreatment ?? true,
      }]
    })
  }

  @computed get hasPhases() {
    return !!this.data.schedule?.length
  }

  @computed get memberDurationOverrideCount() {
    return (this.data.memberOverrides ?? []).filter(o => o.duration != null).length
  }

  @computed get ownerId() {
    return this.data.professionalId
  }
}
